import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../../utils/numbers";
import { useMostViewedVideos } from "./useDashboardData";
import {
  DateRangeFields,
  IncompleteViewsNote,
  PeriodButtons,
  usePeriodFilter,
} from "./PeriodFilter";

const SkeletonRow = () => (
  <li className="dash-rank">
    <span className="dash-rank-position" />
    <div className="dash-skeleton dash-rank-thumb" />
    <div className="dash-rank-body">
      <div
        className="dash-skeleton"
        style={{ width: "60%", height: 14, marginBottom: 8 }}
      />
      <div className="dash-skeleton" style={{ width: 60, height: 12 }} />
    </div>
  </li>
);

/**
 * The 5 most viewed videos (`unique_viewers` from the database) over the last 7 or 30 days,
 * a date range or since the beginning. A row opens the video details.
 */
const MostViewedVideos = () => {
  const { t } = useTranslation();
  const periodFilter = usePeriodFilter();
  const { loading, data } = useMostViewedVideos(periodFilter.filter);
  const videos = data?.videos || [];

  return (
    <section className="dash-card">
      <div className="dash-card-header">
        <h2 className="dash-card-title">{t("Most viewed videos")}</h2>
        <div className="dash-controls">
          <PeriodButtons {...periodFilter} />
        </div>
      </div>

      <DateRangeFields {...periodFilter} />

      {loading ? (
        <ol className="dash-ranking">
          {[0, 1, 2, 3, 4].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </ol>
      ) : videos.length > 0 ? (
        <ol className="dash-ranking">
          {videos.map((video, index) => (
            <li className="dash-rank" key={video._id}>
              <Link
                className="dash-rank-link"
                to={`/dashboard/video-info/${video._id}`}
              >
                <span className="dash-rank-position">{index + 1}</span>
                {video.thumbnail ? (
                  <img
                    className="dash-rank-thumb"
                    src={video.thumbnail}
                    alt=""
                    loading="lazy"
                  />
                ) : (
                  <span className="dash-rank-thumb" aria-hidden="true" />
                )}
                <div className="dash-rank-body">
                  <p className="dash-rank-title" title={video.title}>
                    {video.title}
                  </p>
                  <span
                    className="dash-badge"
                    style={{
                      "--badge-color": video.isShort
                        ? "var(--dash-violet)"
                        : "var(--dash-blue)",
                    }}
                  >
                    {video.isShort ? t("Short") : t("Video")}
                  </span>
                </div>
                <div className="dash-rank-views">
                  <strong>{formatNumber(video.views)}</strong>
                  {t("views")}
                </div>
              </Link>
            </li>
          ))}
        </ol>
      ) : (
        <p className="dash-empty">
          {data || periodFilter.period === "custom"
            ? t("No views over this period")
            : t("Not available")}
        </p>
      )}

      {!loading && <IncompleteViewsNote data={data} />}
    </section>
  );
};

export default MostViewedVideos;
